import { useMemo, useState, ChangeEventHandler, MouseEvent, useEffect } from 'react'
import { useGetIdsHealthcareservicesHcsIdPatients } from '@toktokdoctech/tokapi-client'
import { useNavigate } from 'react-router-dom'
import { CircularProgress, Stack } from '@mui/material'
import TokTokDocDataTable from '../../Common/DataTable'
import { TokTokDocDataTableHandleRowClick } from '../../Common/_props/TokTokDocDataTableProps'
import { PatientsDataTableRow, PatientsDataTableProps } from './_props'
import useCurrentHcs from '../../../hooks/useCurrentHcs'
import PageContentBody from '../../Common/pageContent/PageContentBody'
import { usePatientDataTableConfig } from './usePatientDataTableConfig'
import { useAppDispatch, useAppSelector } from '../../../store'
import { setDefaultPagination } from '../../../actions/main'

const PatientsDataTable = ({
    filters: { search, insiIdentityStatuses, birthDate, attendingPhysicianStatus },
    onMessage,
}: PatientsDataTableProps) => {
    const { id: hcsId } = useCurrentHcs()
    const dispatch = useAppDispatch()
    const initialItemsPerPage = useAppSelector((state) => state.main.pagination.itemsPerPage)
    const navigate = useNavigate()
    const [page, setPage] = useState<number>(0)
    const [limit, setLimit] = useState<number>(5)

    useEffect(() => {
        setLimit(initialItemsPerPage || 5)
    }, [initialItemsPerPage])

    const handleItemsPerPageChange: ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> = (e) => {
        const { value } = e.target
        setLimit(Number(value))
        setPage(0)
        dispatch(setDefaultPagination(value))
    }

    const handlePaginationChange = (_event: MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage)
    }

    const [sort, setSort] = useState<'ascending' | 'descending'>('ascending') // TODO plug it to the api when api is updated

    const [isTableAdvancedModeActivated, setIsTableAdvancedModeActivated] = useState(
        localStorage.getItem('isTableAdvancedModeActivated') === 'true',
    )

    const handleTableModeChange = () => {
        localStorage.setItem('isTableAdvancedModeActivated', (!isTableAdvancedModeActivated).toString())
        setIsTableAdvancedModeActivated(!isTableAdvancedModeActivated)
    }

    const {
        data: patientsData,
        isLoading,
        error,
    } = useGetIdsHealthcareservicesHcsIdPatients(hcsId, {
        limit,
        skip: limit * page,
        search,
        insiIdentityStatuses,
        attendingPhysicianStatus,
        birthDate,
    })

    const handleSort = () => {
        setSort(sort === 'ascending' ? 'descending' : 'ascending')
        setPage(0)
    }

    const { headers, buildRowsMap } = usePatientDataTableConfig({
        handleSort,
        sort,
        isAdvancedModeActivated: isTableAdvancedModeActivated,
    })

    const userRows = useMemo<PatientsDataTableRow[]>(() => {
        if (!patientsData) return []

        return patientsData.patients.map((patient) => ({
            ...patient,
            objectId: patient.id,
        }))
    }, [patientsData])

    useEffect(() => {
        if (!patientsData) return
        if (patientsData.patients.length === 0) {
            onMessage({ text: 'Aucun résultat correspondant à la recherche', level: 'info' })
        } else {
            onMessage(null)
        }
    }, [onMessage, patientsData])

    if (isLoading)
        return (
            <PageContentBody>
                <Stack justifyContent="center" alignItems="center">
                    <CircularProgress />
                </Stack>
            </PageContentBody>
        )
    if (error) throw new Error()

    const handleRowClick: TokTokDocDataTableHandleRowClick<PatientsDataTableRow> = (row) => {
        navigate(`/patient/${row?.id}`)
    }

    return (
        <TokTokDocDataTable
            page={page}
            itemsPerPage={limit}
            count={patientsData?.total}
            handlePaginationChange={handlePaginationChange}
            handleItemsPerPageChange={handleItemsPerPageChange}
            headers={headers}
            items={userRows}
            describeRows={buildRowsMap}
            onRowClick={handleRowClick}
            handleMoreColumns={handleTableModeChange}
            displayMoreColumns={isTableAdvancedModeActivated}
        />
    )
}

export default PatientsDataTable
